import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import {
  getFormularioPoliticaMunicipalAgriculturaFamiliarByUUID
} from 'api/forms/pacote_formularios.api';
import ContainerPainel from 'components/container_painel';
import { Loading } from 'components/login';
import DocumentComponent from 'components/shared/document_component';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useFormularioPoliticaMunicipalAgriculturaFamiliarStore } from '../../../../../stores/forms/FormularioPMAF.store';

const FormPMAFVisualizar: React.FC = () => {
  const { uuid } = useParams();
  const { formulario, setFormulario } = useFormularioPoliticaMunicipalAgriculturaFamiliarStore();
  const [loading, setLoading] = useState(true);

  const getFormulario = async () => {
    try {
      if (!uuid) {
        throw new Error('UUID não fornecido');
      }

      const response = await getFormularioPoliticaMunicipalAgriculturaFamiliarByUUID(uuid);

      if (response.dados) {
        setFormulario(response.dados);
      }
    } catch (error) {
      console.error('Erro ao carregar formulário:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTipoInstrumentoTexto = (valor: number | null) => {
    switch (valor) {
      case 1: return 'Lei';
      case 2: return 'Decreto';
      case 3: return 'Portaria';
      case 4: return 'Resolução';
      default: return 'Não informado';
    }
  };

  const formatarData = (data: string | null) => {
    if (!data) return 'Não informado';
    try {
      return new Date(data).toLocaleDateString('pt-BR');
    } catch {
      return 'Data inválida';
    }
  };

  useEffect(() => {
    getFormulario();
  }, []);

  if (loading) {
    return (
      <ContainerPainel
        pagina='Visualizar Política Municipal da Agricultura Familiar'
        showBackButton={true}
      >
        <Loading />
      </ContainerPainel>
    );
  }

  const possuiPolitica = formulario?.municipio_possui_politica_municipal_de_agricula_familiar_ou_indigena;

  return (
    <ContainerPainel
      pagina='Visualizar Política Municipal da Agricultura Familiar'
      showBackButton={true}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', padding: '2rem' }}>
        <Card sx={{ width: '100%', maxWidth: '800px', boxShadow: 3 }}>
          <Box
            sx={{
              backgroundColor: '#1976d2',
              color: 'white',
              padding: '1.5rem',
              borderTopLeftRadius: '4px',
              borderTopRightRadius: '4px',
              textAlign: 'center',
            }}
          >
            <Typography variant='h6' fontWeight='bold'>
              Política Municipal da Agricultura Familiar
            </Typography>
            <Typography variant='subtitle2'>
              INFORMAÇÕES SOBRE A POLÍTICA MUNICIPAL DA AGRICULTURA FAMILIAR
            </Typography>
          </Box>

          <CardContent sx={{ p: 4 }}>
            <Stack spacing={4}>

              {/* Campo 1: Possui Política Municipal */}
              <Box>
                <Typography variant='h6' gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                  Status da Política Municipal
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant='body1'>
                    O município possui Política Municipal de Agricultura Familiar:
                  </Typography>
                  <Chip
                    label={possuiPolitica === true ? 'SIM' : possuiPolitica === false ? 'NÃO' : 'Não informado'}
                    color={possuiPolitica === true ? 'success' : possuiPolitica === false ? 'error' : 'default'}
                    variant="filled"
                    sx={{ fontWeight: 'bold' }}
                  />
                </Box>
              </Box>

              {/* Campos condicionais - só aparecem se "Sim" */}
              {possuiPolitica === true && (
                <Box
                  sx={{
                    border: '2px solid #e3f2fd',
                    borderRadius: 2,
                    p: 3,
                    backgroundColor: '#fafafa',
                  }}
                >
                  <Typography variant='h6' gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                    Detalhes do Instrumento Legal
                  </Typography>

                  <Stack spacing={3}>
                    {/* Tipo do Instrumento/Dispositivo Legal */}
                    <Box>
                      <Typography variant='subtitle2' color='text.secondary' gutterBottom>
                        Tipo do Instrumento/Dispositivo Legal
                      </Typography>
                      <Typography variant='body1' sx={{ fontWeight: 'bold' }}>
                        {getTipoInstrumentoTexto(formulario?.tipo_de_instrumento ?? null)}
                      </Typography>
                    </Box>

                    {/* Número da Publicação */}
                    <Box>
                      <Typography variant='subtitle2' color='text.secondary' gutterBottom>
                        Número da Publicação
                      </Typography>
                      <Typography variant='body1' sx={{ fontWeight: 'bold' }}>
                        {formulario?.numero_da_publicacao || 'Não informado'}
                      </Typography>
                    </Box>

                    {/* Data da Publicação */}
                    <Box>
                      <Typography variant='subtitle2' color='text.secondary' gutterBottom>
                        Data da Publicação
                      </Typography>
                      <Typography variant='body1' sx={{ fontWeight: 'bold' }}>
                        {formatarData(formulario?.data_da_publicacao ?? null)}
                      </Typography>
                    </Box>

                    {/* Documento */}
                    <Box>
                      <Typography variant='subtitle2' color='text.secondary' gutterBottom>
                        Documento da Política Municipal
                      </Typography>
                      {formulario?.upload_documento ? (
                        <DocumentComponent
                          uuid={formulario.upload_documento}
                          setUuid={() => { }}
                          podeSubstituir={false}
                          podeEnviar={false}
                        />
                      ) : (
                        <Typography variant='body2' color='text.secondary' sx={{ fontStyle: 'italic' }}>
                          Nenhum documento disponível
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Box>
              )}


              <Box>
                <Typography variant='h6' gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                  Plano Municipal da Agricultura Familiar – PMAF
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant='subtitle2' color='text.secondary' gutterBottom>
                    O município possui o Plano Municipal da Agricultura Familiar – PMAF?
                  </Typography>
                  <Chip
                    label={formulario?.possui_plano_municipal_agricultura_familiar === true ? 'SIM' : formulario?.possui_plano_municipal_agricultura_familiar === false ? 'NÃO' : 'Não informado'}
                    color={possuiPolitica === true ? 'success' : possuiPolitica === false ? 'error' : 'default'}
                    variant="filled"
                    sx={{ fontWeight: 'bold' }}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  border: '2px solid #e3f2fd',
                  borderRadius: 2,
                  p: 3,
                  backgroundColor: '#fafafa',
                  mt: 4,
                  display: formulario?.possui_plano_municipal_agricultura_familiar === true ? 'block' : 'none'
                }}
              >

                <Stack spacing={3}>
                  {/* Documento do Plano */}
                  {formulario?.possui_plano_municipal_agricultura_familiar === true && (
                    <Box>
                      <DocumentComponent
                        uuid={formulario.upload_plano_pmaf}
                        title='Documento do Plano Municipal da Agricultura Familiar (PDF)'
                        setUuid={() => { }}
                        podeSubstituir={false}
                        podeEnviar={false}
                      />
                    </Box>
                  )}
                </Stack>
              </Box>


              {/* Mensagem quando não há informação */}
              {possuiPolitica === null || possuiPolitica === undefined && (
                <Box
                  sx={{
                    border: '2px solid #fff3e0',
                    borderRadius: 2,
                    p: 3,
                    backgroundColor: '#fafafa',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant='body1' color='text.secondary'>
                    Informação não disponível.
                  </Typography>
                  <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                    O formulário ainda não foi preenchido.
                  </Typography>
                </Box>
              )}

            </Stack>
          </CardContent>
        </Card>
      </Box>
    </ContainerPainel>
  );
};

export default FormPMAFVisualizar;
